import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const Navbar = ({ onLogout }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false); // Quick Action Settings State
  const [showProfileSettingsSubmenu, setShowProfileSettingsSubmenu] = useState(false); // Profile Submenu State

  const [notifications] = useState([
    { id: 1, type: "info", message: "New patient registered: John Doe", time: "5m ago", icon: "bi-person-plus" },
    { id: 2, type: "warning", message: "Low stock alert: Paracetamol 500mg", time: "15m ago", icon: "bi-exclamation-triangle" },
    { id: 3, type: "success", message: "Payment received: ₹5,000", time: "1h ago", icon: "bi-cash-coin" },
  ]);
  const [unreadCount] = useState(3);
  const [searchQuery, setSearchQuery] = useState("");
  const [hospitalName, setHospitalName] = useState("");
  const [userName, setUserName] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());

  // --- ZOOM STATE ---
  const [zoomLevel, setZoomLevel] = useState(100); 

  const profileRef = useRef(null);
  const notificationRef = useRef(null);
  const settingsRef = useRef(null); // Quick Actions Settings Ref
  const navigate = useNavigate();

  useEffect(() => {
    const storedHospital = sessionStorage.getItem("hospitalName") || "Healthcare Management";
    const storedUser = sessionStorage.getItem("loginId") || "Admin";
    setHospitalName(storedHospital);
    setUserName(storedUser);

    // Update time every second
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);

    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
        setShowProfileSettingsSubmenu(false); // Close submenu on outside click
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettingsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearInterval(timer);
    };
  }, []);

  // --- ZOOM LOGIC ---
  const updateZoom = (newZoom) => {
    setZoomLevel(newZoom);
    document.body.style.zoom = `${newZoom}%`;
  };

  const handleZoomIn = () => {
    if (zoomLevel < 200) updateZoom(zoomLevel + 10);
  };

  const handleZoomOut = () => {
    if (zoomLevel > 50) updateZoom(zoomLevel - 10);
  };

  const handleResetZoom = () => {
    updateZoom(100);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log("Searching for:", searchQuery);
    }
  };

  const formatTime = () => {
    return currentTime.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatDate = () => {
    return currentTime.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <nav className="navbar-enhanced">
      <div className="navbar-content">
        {/* Left: Logo & Hospital Name */}
        <div className="navbar-left">
          <div className="navbar-brand">
            <div className="brand-logo">
              <i className="bi bi-hospital"></i>
            </div>
            <div className="brand-text">
              <h1>{hospitalName}</h1>
              <span>&copy; KeyCodeTech.Pvt.Ltd</span>   
            </div>
          </div>
        </div>

        {/* Center: Search */}
        <div className="navbar-center">
          <form className="search-form" onSubmit={handleSearch}>
            <i className="bi bi-search search-icon"></i>
            <input
              type="search"
              className="search-input"
              placeholder="Search patients, medicines, bills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-btn">
              Search
            </button>
          </form>
        </div>

        {/* Right: Quick Actions & Profile */}
        <div className="navbar-right">
          
          {/* --- ZOOM CONTROLS --- */}
          <div className="zoom-controls">
            <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out">
              <i className="bi bi-dash"></i>
            </button>
            <span className="zoom-display" onClick={handleResetZoom} title="Reset Zoom">
              {zoomLevel}%
            </span>
            <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In">
              <i className="bi bi-plus"></i>
            </button>
          </div>
          <div className="navbar-divider"></div>

          {/* Date & Time Display */}
          <div className="datetime-widget">
            <div className="time-display">{formatTime()}</div>
            <div className="date-display">{formatDate()}</div>
          </div>

          <div className="navbar-divider"></div>

          {/* Quick Actions */}
          <div className="quick-actions">
            {/* Messages */}
            <button className="action-btn" title="Messages">
              <i className="bi bi-chat-dots"></i>
              <span className="badge">2</span>
            </button>

            {/* Notifications */}
            <div className="action-dropdown" ref={notificationRef}>
              <button
                className="action-btn"
                onClick={() => setShowNotifications(!showNotifications)}
                title="Notifications"
              >
                <i className="bi bi-bell"></i>
                {unreadCount > 0 && <span className="badge pulse">{unreadCount}</span>}
              </button>

              {showNotifications && (
                <div className="dropdown-panel notifications-panel">
                  <div className="panel-header">
                    <h6>Notifications</h6>
                    <span className="badge-pill">{unreadCount} New</span>
                  </div>
                  <div className="panel-body">
                    {notifications.map((notif) => (
                      <div key={notif.id} className="notification-item">
                        <div className={`notif-icon ${notif.type}`}>
                          <i className={`bi ${notif.icon}`}></i>
                        </div>
                        <div className="notif-content">
                          <p>{notif.message}</p>
                          <span>{notif.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="panel-footer">
                    <button>View All Notifications</button>
                  </div>
                </div>
              )}
            </div>

            {/* Calendar */}
            <button className="action-btn" title="Calendar">
              <i className="bi bi-calendar3"></i>
            </button>

            {/* 🔥 NEW: Quick Actions Settings Dropdown */}
            <div 
              className="action-dropdown" 
              ref={settingsRef}
              onMouseEnter={() => setShowSettingsMenu(true)} 
              onMouseLeave={() => setShowSettingsMenu(false)}
            >
              <button 
                className="action-btn" 
                title="Settings"
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
              >
                <i className="bi bi-gear"></i>
              </button>

              {showSettingsMenu && (
                <div className="dropdown-panel settings-panel" style={{ minWidth: "220px", top: "calc(100% + 5px)" }}>
                  <div className="panel-body" style={{ padding: "8px 0" }}>
                    <button 
                      className="panel-item" 
                      onClick={() => { navigate("/ReceiptSettings"); setShowSettingsMenu(false); }}
                    >
                      <i className="bi bi-receipt"></i>
                      <span>Receipt Footer Setting</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="navbar-divider"></div>

          {/* Profile */}
          <div className="action-dropdown" ref={profileRef}>
            <button
              className="profile-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div className="profile-avatar">
                <i className="bi bi-person"></i>
              </div>
              <div className="profile-details">
                <span className="profile-name">{userName}</span>
                <span className="profile-role">Administrator</span>
              </div>
              <i className="bi bi-chevron-down"></i>
            </button>

            {showProfileMenu && (
              <div className="dropdown-panel profile-panel">
                <div className="panel-header profile-header">
                  <div className="profile-avatar-large">
                    <i className="bi bi-person"></i>
                  </div>
                  <div>
                    <h6>{userName}</h6>
                    <p>admin@hospital.com</p>
                  </div>
                </div>
                <div className="panel-body" style={{ padding: "8px 0" }}>
                  <button className="panel-item" onClick={() => { navigate("/profile"); setShowProfileMenu(false); }}>
                    <i className="bi bi-person"></i>
                    <span>My Profile</span>
                  </button>
                  
                  {/* 🔥 NEW: Profile Settings Submenu with Hover & Click */}
                  <div 
                    onMouseEnter={() => setShowProfileSettingsSubmenu(true)}
                    onMouseLeave={() => setShowProfileSettingsSubmenu(false)}
                  >
                    <button 
                      className="panel-item" 
                      onClick={() => setShowProfileSettingsSubmenu(!showProfileSettingsSubmenu)}
                      style={{ justifyContent: "space-between" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                        <i className="bi bi-gear"></i>
                        <span>Settings</span>
                      </div>
                      <i className={`bi bi-chevron-${showProfileSettingsSubmenu ? 'up' : 'down'}`} style={{ fontSize: '12px', width: 'auto' }}></i>
                    </button>
                    
                    {/* Nested Submenu Items */}
                    {showProfileSettingsSubmenu && (
                      <div className="submenu-container" style={{ background: "#f8f9fa", padding: "5px 0" }}>
                        <button 
                          className="panel-item submenu-item" 
                          onClick={() => { navigate("/ReceiptSettings"); setShowProfileMenu(false); setShowProfileSettingsSubmenu(false); }}
                        >
                          <i className="bi bi-receipt" style={{ fontSize: "16px" }}></i>
                          <span>Receipt Footer Setting</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <button className="panel-item" onClick={() => { navigate("/help"); setShowProfileMenu(false); }}>
                    <i className="bi bi-question-circle"></i>
                    <span>Help & Support</span>
                  </button>
                </div>
                <div className="panel-footer">
                  <button className="logout-panel-btn" onClick={onLogout}>
                    <i className="bi bi-box-arrow-right"></i>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    <style jsx>{`
        .navbar-enhanced {
          background: white;
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
          position: sticky;
          top: 0;
          z-index: 999;
          border-bottom: 3px solid var(--theme-color);
        }

        .navbar-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: clamp(4px, 0.4vw, 6px) clamp(12px, 1.2vw, 20px);
          gap: clamp(8px, 0.8vw, 12px);
          max-width: 100%;
        }

        .zoom-controls {
          display: flex; align-items: center; background: #f0f4f8;
          border-radius: 50px; padding: 4px; border: 1px solid #e0e6ed;
        }

        .zoom-btn {
          width: 32px; height: 32px; border-radius: 50%; border: none;
          background: white; color: var(--theme-color);
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s; box-shadow: 0 2px 5px rgba(0,0,0,0.05);
        }

        .zoom-btn:hover { background: var(--theme-color); color: white; transform: scale(1.1); }

        .zoom-display { font-size: 13px; font-weight: 700; color: #2c3e50; min-width: 45px; text-align: center; cursor: pointer; user-select: none; }
        .zoom-display:hover { color: var(--theme-color); }

        .navbar-left { flex-shrink: 0; }
        .navbar-brand { display: flex; align-items: center; gap: 15px; }
        
        .brand-logo {
          width: clamp(28px, 2.2vw, 32px); height: clamp(28px, 2.2vw, 32px);
          background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
          border-radius: clamp(6px, 0.6vw, 8px); display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }
        .brand-logo i { font-size: clamp(14px, 1.4vw, 18px); color: white; }
        .brand-text h1 { margin: 0; font-size: clamp(12px, 1vw, 14px); font-weight: 700; color: #2c3e50; line-height: 1.2; }
        .brand-text span { font-size: clamp(8px, 0.65vw, 9px); color: #7f8c8d; font-weight: 500; }

        .navbar-center { flex: 1; max-width: clamp(300px, 35vw, 500px); }
        .search-form {
          display: flex; align-items: center; background: #f0f4f8;
          border-radius: 50px; padding: clamp(2px, 0.25vw, 4px) clamp(6px, 0.6vw, 8px);
          border: 2px solid transparent; transition: all 0.3s ease;
        }
        .search-form:focus-within { background: white; border-color: var(--theme-color); box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08); }
        .search-icon { color: #7f8c8d; font-size: clamp(12px, 1vw, 14px); margin: 0 clamp(6px, 0.6vw, 8px); }
        .search-input { flex: 1; border: none; outline: none; background: transparent; font-size: clamp(11px, 0.85vw, 12px); color: #2c3e50; padding: clamp(2px, 0.25vw, 4px) 0; min-width: 0; }
        .search-input::placeholder { color: #95a5a6; }
        
        .search-btn {
          padding: clamp(4px, 0.4vw, 6px) clamp(12px, 1.2vw, 16px); border-radius: 50px; border: none;
          background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
          color: white; font-size: clamp(10px, 0.8vw, 11px); font-weight: 600; cursor: pointer; transition: all 0.3s ease; white-space: nowrap;
        }
        .search-btn:hover { transform: scale(1.05); box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); }

        .navbar-right { display: flex; align-items: center; gap: clamp(6px, 0.6vw, 10px); flex-shrink: 0; }
        .datetime-widget { text-align: right; min-width: fit-content; }
        .time-display { font-size: clamp(10px, 0.8vw, 11px); font-weight: 700; color: var(--theme-color); line-height: 1.2; }
        .date-display { font-size: clamp(8px, 0.65vw, 9px); color: #7f8c8d; font-weight: 500; }
        .navbar-divider { width: 1px; height: clamp(24px, 2vw, 28px); background: #e0e6ed; }

        .quick-actions { display: flex; gap: clamp(4px, 0.5vw, 6px); }
        .action-btn {
          position: relative; width: clamp(28px, 2.2vw, 32px); height: clamp(28px, 2.2vw, 32px); 
          border-radius: clamp(6px, 0.6vw, 8px); border: none;
          background: #f0f4f8; color: var(--theme-color); cursor: pointer; display: flex;
          align-items: center; justify-content: center; transition: all 0.3s ease;
        }
        .action-btn:hover { background: #e0eaf3; transform: translateY(-2px); color: var(--theme-color-dark); }
        .action-btn i { font-size: clamp(14px, 1.2vw, 16px); }
        
        .badge { position: absolute; top: -4px; right: -4px; background: #e74c3c; color: white; font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 10px; min-width: 18px; }
        .badge.pulse { animation: pulse-badge 2s infinite; }
        @keyframes pulse-badge { 0%, 100% { box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7); } 50% { box-shadow: 0 0 0 8px rgba(231, 76, 60, 0); } }

        .profile-btn {
          display: flex; align-items: center; gap: clamp(6px, 0.6vw, 8px); 
          padding: clamp(2px, 0.25vw, 4px) clamp(6px, 0.6vw, 8px); border-radius: 50px;
          border: none; background: #f0f4f8; cursor: pointer; transition: all 0.3s ease;
        }
        .profile-btn:hover { background: #e0eaf3; }
        
        .profile-avatar {
          width: clamp(24px, 2vw, 28px); height: clamp(24px, 2vw, 28px); border-radius: 50%;
          background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
          display: flex; align-items: center; justify-content: center; color: white;
        }
        .profile-avatar i { font-size: clamp(12px, 1.1vw, 14px); }
        .profile-details { display: flex; flex-direction: column; text-align: left; }
        .profile-name { font-size: clamp(10px, 0.8vw, 11px); font-weight: 600; color: #2c3e50; line-height: 1.2; }
        .profile-role { font-size: clamp(8px, 0.65vw, 9px); color: #7f8c8d; }

        .action-dropdown { position: relative; }
        .dropdown-panel {
          position: absolute; top: calc(100% + 12px); right: 0; background: white;
          border-radius: 16px; box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
          min-width: 320px; z-index: 1000; animation: slideDown 0.3s ease; border: 1px solid #e0e6ed;
        }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        
        .panel-header { padding: 20px; border-bottom: 1px solid #f0f4f8; display: flex; justify-content: space-between; align-items: center; }
        .panel-header h6 { margin: 0; font-size: 16px; font-weight: 600; color: #2c3e50; }
        .badge-pill { padding: 4px 12px; border-radius: 20px; background: var(--theme-color); color: white; font-size: 11px; font-weight: 600; }
        
        .panel-body { max-height: 350px; overflow-y: auto; }
        .panel-body::-webkit-scrollbar { width: 6px; }
        .panel-body::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
        .panel-footer { padding: 12px 20px; border-top: 1px solid #f0f4f8; }
        
        .panel-footer button {
          width: 100%; padding: 10px; border: none;
          background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
          color: white; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.3s ease;
        }
        .panel-footer button:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); }

        .notification-item { padding: 16px 20px; display: flex; gap: 15px; border-bottom: 1px solid #f8f9fa; cursor: pointer; transition: all 0.2s ease; }
        .notification-item:hover { background: #f8f9fa; }
        .notif-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .notif-icon.info { background: #e3f2fd; color: #2196f3; }
        .notif-icon.warning { background: #fff3cd; color: #ffc107; }
        .notif-icon.success { background: #d4edda; color: #28a745; }
        .notif-icon i { font-size: 20px; }
        .notif-content { flex: 1; }
        .notif-content p { margin: 0 0 6px 0; font-size: 13px; color: #2c3e50; font-weight: 500; }
        .notif-content span { font-size: 11px; color: #95a5a6; }

        .profile-header { flex-direction: column; align-items: center; text-align: center; gap: 12px; }
        .profile-avatar-large {
          width: 70px; height: 70px; border-radius: 50%;
          background: linear-gradient(135deg, var(--theme-color) 0%, var(--theme-color-dark) 100%);
          display: flex; align-items: center; justify-content: center; color: white;
        }
        .profile-avatar-large i { font-size: 36px; }
        .profile-header h6 { margin: 0; font-size: 18px; }
        .profile-header p { margin: 0; font-size: 13px; color: #7f8c8d; }
        
        .panel-item {
          width: 100%; padding: 14px 20px; border: none; background: transparent; text-align: left;
          display: flex; align-items: center; gap: 15px; cursor: pointer; transition: all 0.2s ease; color: #2c3e50;
        }
        .panel-item:hover { background: #f8f9fa; }
        .panel-item i { font-size: 20px; width: 24px; color: var(--theme-color); }
        
        /* 🔥 NEW: Indented styling for Nested Submenu Item */
        .submenu-item { padding-left: 45px; font-size: 14px; border-left: 3px solid transparent; }
        .submenu-item:hover { border-left: 3px solid var(--theme-color); background: #eef2f5; }

        .logout-panel-btn {
          width: 100%; padding: 12px; border: none;
          background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
          color: white; border-radius: 10px; font-size: 14px; font-weight: 600;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: all 0.3s ease;
        }
        .logout-panel-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(231, 76, 60, 0.4); }

        @media (max-width: 768px) {
          .navbar-content { padding: 10px 15px; gap: 10px; }
          .datetime-widget, .zoom-controls, .brand-text span { display: none; }
          .navbar-center { display: none; }
          .brand-logo { width: 36px; height: 36px; }
          .brand-logo i { font-size: 18px; }
          .brand-text h1 { font-size: 16px; }
          .navbar-right { gap: 8px; }
          .action-btn { width: 36px; height: 36px; }
          .action-btn i { font-size: 18px; }
          .profile-details, .profile-btn i.bi-chevron-down { display: none; }
          .profile-btn { padding: 0; background: transparent; }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;